/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 拍照路线状态管理 (RTK Slice)
 * @LastEditors: refactor
 * @LastEditTime: 2026-03-25
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs, { Dayjs } from 'dayjs'
import CONFIG from '../constants/config'

// 照片类型
interface PathwayPhoto {
  photoID?: string | number
  smallurl?: string
  largeurl?: string
  time?: number
  mediaType?: number
  videoURL?: string
  sourceType?: number
  [key: string]: any
}

// 位置信息类型
interface PathwayLocation {
  photos: PathwayPhoto[]
  texts: {
    addr?: string
    consumptionOfTime?: string
    predictWalk?: string
    predictDrive?: string
    [key: string]: any
  }
  [key: string]: any
}

// 路线数据类型
interface Pathway {
  userID: string | number
  nickname?: string
  locations: PathwayLocation[]
  [key: string]: any
}

// 成员类型
interface PathwayMember {
  userID: string | number
  nickname?: string
  headimgurl?: string
  [key: string]: any
}

// 查看器照片类型
interface ViewerPhoto {
  smallURL?: string
  largeURL?: string
  mediaType?: number
  saveName?: string
  videoURL?: string
  sourceType?: number
  seq?: string | number
  [key: string]: any
}

// 面板类型
type PanelType = typeof CONFIG.pathwayPanelTypes.SHOT_PANEL | typeof CONFIG.pathwayPanelTypes.NOT_SHOT_PANEL

// State 类型
interface PathwayState {
  date: Dayjs | null
  currentUser: PathwayMember | null
  takeMembers: PathwayMember[]
  unTakeMembers: PathwayMember[]
  pathways: Pathway[]
  checkedMembers: (string | number)[]
  panel: PanelType
  viewerPhotos: ViewerPhoto[]
}

const initialState: PathwayState = {
  date: dayjs(),
  currentUser: null,
  takeMembers: [],
  unTakeMembers: [],
  pathways: [],
  checkedMembers: [],
  panel: CONFIG.pathwayPanelTypes.SHOT_PANEL,
  viewerPhotos: []
}

const pathwaySlice = createSlice({
  name: 'pathway',
  initialState,
  reducers: {
    // 设置日期
    setDate(state, action: PayloadAction<Dayjs>) {
      state.date = action.payload
    },

    // 设置路线数据
    setPathways(state, action: PayloadAction<Pathway[]>) {
      state.pathways = action.payload
    },

    // 设置成员列表（已拍照 + 未拍照）
    setMembers(
      state,
      action: PayloadAction<{ peoplesTake: PathwayMember[]; peoplesUnTake: PathwayMember[] }>
    ) {
      state.takeMembers = action.payload.peoplesTake
      state.unTakeMembers = action.payload.peoplesUnTake
    },

    // 设置已勾选的成员
    setCheckedMembers(state, action: PayloadAction<(string | number)[]>) {
      state.checkedMembers = action.payload
    },

    // 设置当前用户
    setCurrentUser(state, action: PayloadAction<PathwayMember | null>) {
      state.currentUser = action.payload
    },

    // 设置查看器照片列表
    setViewerPhotos(state, action: PayloadAction<ViewerPhoto[]>) {
      state.viewerPhotos = action.payload
    },

    // 设置用户列表面板类型
    setUserListPanel(state, action: PayloadAction<PanelType>) {
      state.panel = action.payload
    },

    // 清除所有路线数据（重置为初始状态）
    clearPathway(state) {
      state.date = dayjs()
      state.currentUser = null
      state.takeMembers = []
      state.unTakeMembers = []
      state.pathways = []
      state.checkedMembers = []
      state.panel = CONFIG.pathwayPanelTypes.SHOT_PANEL
      state.viewerPhotos = []
    }
  }
})

export const {
  setDate,
  setPathways,
  setMembers,
  setCheckedMembers,
  setCurrentUser,
  setViewerPhotos,
  setUserListPanel,
  clearPathway
} = pathwaySlice.actions

export default pathwaySlice.reducer

// 类型导出，供组件使用
export type { PathwayState, Pathway, PathwayMember, PathwayLocation, PathwayPhoto, ViewerPhoto }
